import { useCallback, useEffect, useState } from 'react'
import { useMutation } from '@apollo/client'
import { Button } from '@resiliatech/birdsong'
import { USER_SURVEY } from 'Apollo/Queries/UserSurvey'
import { UPSERT_USER_SURVEY } from 'Apollo/Mutations/UpsertUserSurvey'
import { useAppState } from 'Context/AppContext/AppContext'
import { SurveyType, useUserSurveyQuery } from 'Generated/graphql-types'
import { Priorities } from 'Components/SharedComponents/Surveys/types'
import {
  BackButton,
  FullWindowContainer,
  Header,
  SurveyContainer,
  WizardContainer,
  WizardSteps,
} from 'Components/SharedComponents/Surveys/Surveys.styled'
import { handleToggleGoals } from 'Components/SharedComponents/Surveys/utils'
import { useHideNavigation } from 'Hooks/useHideNavigation'
import { useSnoozeCheckInSurvey } from 'Hooks/useSnoozeCheckInSurvey'
import {
  checkIn as checkInSurveyAnalytics,
  CheckInSurveyEvent,
  CheckInSurveyParams,
} from 'Modules/Analytics/trackActionInterface/Surveys/checkIn'
import { ResiliaIconWhite } from '../../Views/AppBar/AppLogoBlue'
import { Loading } from '../Academy/_common/Loading'
import { createWizardConfig } from './config'
import { CheckInWizardStep } from './constants'
import { HandleActionArgs, PartialUserSurvey } from './types'
import { initialPriorities } from '../NeedsAssessment/constants'
import { isCheckInSurveyComplete } from './utils'

export const CheckInSurvey = () => {
  const {
    nonprofitOrgConfig: {
      funderOrgId,
      orgId: nonprofitOrgId,
      loading: nonprofitOrgConfigLoading,
      platformPlan,
    },
    userConfig: { id: userId, email },
  } = useAppState()
  useHideNavigation()
  const {
    loading: isCheckInSurveyLoading,
    isEligibleForSurvey,
    snoozeSurvey,
  } = useSnoozeCheckInSurvey()
  const [checkInSurveyComments, setCheckInSurveyComments] = useState('')
  const [currentStep, setCurrentStep] = useState(CheckInWizardStep.GET_STARTED)
  const [loading, setLoading] = useState(false)
  const [priorities, setPriorities] = useState(initialPriorities as Priorities)
  const [orgHealthChange, setOrgHealthChange] = useState('')
  const [orgHealthChangeExplanation, setOrgHealthChangeExplanation] =
    useState('')
  const [orgMissionChange, setOrgMissionChange] = useState('')
  const [orgMissionChangeExplanation, setOrgMissionChangeExplanation] =
    useState<string>('')

  const [shouldLoadSavedProgress, setShouldLoadSavedProgress] = useState(true)
  const [userJourney, setUserJourney] = useState<CheckInWizardStep[]>([
    CheckInWizardStep.GET_STARTED,
  ])

  const {
    data: { userSurvey: originalUserSurvey } = {},
    loading: surveyLoading,
  } = useUserSurveyQuery({
    skip: !nonprofitOrgId || isCheckInSurveyLoading,
    variables: { isCompleted: true, nonprofitOrgId },
  })

  const {
    data: { userSurvey: latestSurvey } = {},
    loading: latestSurveyLoading,
  } = useUserSurveyQuery({
    skip: !nonprofitOrgId || isCheckInSurveyLoading,
    variables: { nonprofitOrgId },
  })

  const redirectHome = useCallback(() => {
    window.location.href = '/home'
  }, [])

  const isCompleted =
    currentStep === CheckInWizardStep.YOUR_ORG_HEALTH && // Second to last step of wizard
    isCheckInSurveyComplete({
      orgMissionChange,
      orgHealthChange,
      priorities,
    })

  const getSurveyIdToUpdate = useCallback(() => {
    if (!latestSurvey) return undefined
    // check-in survey that is in progress
    if (
      (latestSurvey.type === SurveyType.FourMonthCheckIn ||
        latestSurvey.type === SurveyType.ThreeMonthCheckIn) &&
      !latestSurvey.completedAt
    )
      return latestSurvey.id

    // Survey that was just marked as completed, but we are saving comments in last step
    if (
      (latestSurvey.type === SurveyType.FourMonthCheckIn ||
        latestSurvey.type === SurveyType.ThreeMonthCheckIn) &&
      latestSurvey.completedAt &&
      currentStep === CheckInWizardStep.THANK_YOU
    )
      return latestSurvey.id

    return undefined
  }, [currentStep, latestSurvey])

  const [upsertUserSurvey] = useMutation(UPSERT_USER_SURVEY, {
    variables: {
      userSurveyData: {
        id: getSurveyIdToUpdate(),
        nonprofitOrgId,
        response: {
          checkInSurveyComments: checkInSurveyComments || undefined,
          isCompleted,
          orgHealthChange: orgHealthChange === '' ? undefined : orgHealthChange,
          orgHealthChangeExplanation: orgHealthChangeExplanation || undefined,
          orgMissionChange:
            orgMissionChange === '' ? undefined : orgMissionChange,
          orgMissionChangeExplanation: orgMissionChangeExplanation || undefined,
          primaryFunction: originalUserSurvey?.primaryFunction,
          priorities,
          userJourney,
        },
        role: originalUserSurvey?.role,
        type: SurveyType.ThreeMonthCheckIn,
      },
    },
    refetchQueries: [USER_SURVEY],
  })

  useEffect(() => {
    if (!isCheckInSurveyLoading && !isEligibleForSurvey) redirectHome()
  }, [isCheckInSurveyLoading, redirectHome, isEligibleForSurvey])

  // Load the user's previously saved journey if it exists
  useEffect(() => {
    if (!shouldLoadSavedProgress || !originalUserSurvey || !latestSurvey) return
    setLoading(true)

    const surveyToLoad = latestSurvey || originalUserSurvey
    const progress = surveyToLoad?.response
    const priority = progress?.priorities
    const missionChange = progress?.orgMissionChange
    const missionChangeExplanation = progress?.orgMissionChangeExplanation
    const step = progress?.userJourney ? progress.userJourney.at(-1) : undefined
    const journey = progress?.userJourney

    // Could be a step from another version of a survey being loaded (e.g. Needs Assessment)
    const isCheckInSurveyStep = Object.values(CheckInWizardStep).includes(step)
    if (isCheckInSurveyStep && !surveyToLoad?.completedAt) setCurrentStep(step)
    if (priority) setPriorities(priority)
    if (missionChange) setOrgMissionChange(missionChange)
    if (missionChangeExplanation)
      setOrgMissionChangeExplanation(missionChangeExplanation)
    if (surveyToLoad.type === SurveyType.FourMonthCheckIn && journey)
      setUserJourney(journey)

    setShouldLoadSavedProgress(false)
    setLoading(false)
  }, [shouldLoadSavedProgress, originalUserSurvey, latestSurvey])

  useEffect(() => {
    setLoading(
      surveyLoading || latestSurveyLoading || nonprofitOrgConfigLoading
    )
  }, [surveyLoading, nonprofitOrgConfigLoading, latestSurveyLoading])

  const handleAnyAction = ({
    nextStep,
    callback,
    saveUserState,
  }: HandleActionArgs) => {
    // Prevent future renders from loading the saved progress as user progresses through wizard
    if (shouldLoadSavedProgress) setShouldLoadSavedProgress(false)
    if (callback) callback()
    if (originalUserSurvey && saveUserState) upsertUserSurvey()
    if (nextStep && wizard[nextStep]) {
      setUserJourney([...userJourney, nextStep])
      setCurrentStep(nextStep)
    }
  }

  const fireAnalytics = (eventType: keyof typeof checkInSurveyAnalytics) => {
    const analyticsProperties: CheckInSurveyParams = {
      email,
      funderOrgId,
      nonprofitOrgId,
      platform: 'nonprofit',
      platformPlan,
      userId,
    }

    checkInSurveyAnalytics[eventType](analyticsProperties)
  }

  const handleSnoozeSurvey = () => {
    fireAnalytics(CheckInSurveyEvent.Survey_Dismissed)
    snoozeSurvey()
    redirectHome()
  }

  const handleBack = () => {
    // Redirect to home if on first step and click "Not Now"
    // Go back a step if not on first step
    if (userJourney.length >= 2) {
      const previousStep = userJourney[userJourney.length - 2]
      setUserJourney(userJourney.slice(0, -1))
      setCurrentStep(previousStep)
    }
  }

  const wizard = createWizardConfig({
    checkInSurveyComments,
    onAnyAction: handleAnyAction,
    onChangeCheckInSurveyComments: setCheckInSurveyComments,
    onChangeOrgHealth: setOrgHealthChange,
    onChangeOrgHealthChange: setOrgHealthChangeExplanation,
    onFireAnalytics: fireAnalytics,
    orgMissionChange,
    orgMissionChangeExplanation,
    onSetOrgMissionChange: setOrgMissionChange,
    onSetOrgMissionChangeExplanation: setOrgMissionChangeExplanation,
    onSetPriorities: setPriorities,
    onToggleTemplateGoal: handleToggleGoals,
    orgHealthChange,
    orgHealthChangeExplanation,
    priorities,
    userSurvey: originalUserSurvey as PartialUserSurvey,
  })

  const step = wizard[currentStep]

  if (!originalUserSurvey || isCheckInSurveyLoading) return <Loading />

  return (
    <FullWindowContainer>
      <Header>
        <ResiliaIconWhite />
      </Header>
      <SurveyContainer>
        {loading ? (
          <Loading height='300px' />
        ) : (
          <WizardContainer>
            <WizardSteps>{step.component}</WizardSteps>
            {!step.hideBackButton && (
              <BackButton>
                <Button
                  label={
                    currentStep === CheckInWizardStep.GET_STARTED
                      ? 'Not now'
                      : 'Back'
                  }
                  onClick={
                    currentStep === CheckInWizardStep.GET_STARTED
                      ? handleSnoozeSurvey
                      : handleBack
                  }
                  variant='tertiary'
                />
              </BackButton>
            )}
          </WizardContainer>
        )}
      </SurveyContainer>
    </FullWindowContainer>
  )
}
